import React, { useEffect, useState, useCallback } from "react";
import api from "@/api";
import { useAuth } from "@/contexts/AuthContext";
import { Download, RefreshCw, AlertCircle, BarChart2, FileText, Zap, TrendingUp, TrendingDown } from "lucide-react";

const STATUS_COLORS = {
  review: "bg-[#E5F0F2] text-[#006080]",
  approved: "bg-emerald-50 text-emerald-700",
  error: "bg-red-50 text-red-700",
  processing: "bg-amber-50 text-amber-700",
  rejected: "bg-slate-100 text-slate-500",
};

function CreditBar({ label, used, total, overage }) {
  const pct = Math.min(100, (used / Math.max(1, total)) * 100);
  const isOver = overage > 0;
  const isWarning = pct >= 80 && !isOver;
  const color = isOver ? "bg-red-500" : isWarning ? "bg-amber-500" : "bg-[#006080]";

  return (
    <div className="p-4 bg-white border border-slate-200 rounded-lg space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-xs font-bold uppercase tracking-wide text-slate-500">{label}</p>
        {isOver && (
          <span className="text-[10px] font-bold bg-red-100 text-red-700 px-1.5 py-0.5 rounded-full uppercase tracking-wide">
            Overage
          </span>
        )}
        {isWarning && (
          <span className="text-[10px] font-bold bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full uppercase tracking-wide">
            Warning
          </span>
        )}
      </div>
      <div className="w-full h-2.5 bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <div className="flex items-center justify-between text-xs">
        <span className="text-slate-500">{used.toLocaleString()} used / {total.toLocaleString()} total</span>
        <span className={`font-bold ${isOver ? "text-red-600" : "text-slate-700"}`}>
          {isOver ? `+${overage.toLocaleString()} over` : `${(total - used).toLocaleString()} left`}
        </span>
      </div>
      <p className="text-xs text-slate-400">{pct.toFixed(1)}% utilization</p>
    </div>
  );
}

function StatCard({ label, value, sub, Icon, accent = false }) {
  return (
    <div className={`p-4 rounded-lg border ${accent ? "bg-[#006080] border-[#004B66] text-white" : "bg-white border-slate-200"}`}>
      <div className="flex items-start justify-between mb-2">
        <p className={`text-xs font-bold uppercase tracking-wide ${accent ? "text-white/70" : "text-slate-500"}`}>{label}</p>
        <Icon size={15} className={accent ? "text-white/60" : "text-slate-300"} />
      </div>
      <p className={`text-2xl font-black ${accent ? "text-white" : "text-slate-900"}`}>{value}</p>
      {sub && <p className={`text-xs mt-1 ${accent ? "text-white/60" : "text-slate-400"}`}>{sub}</p>}
    </div>
  );
}

export default function CompanyReports() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "superadmin";

  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState("");
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return d.toISOString().slice(0, 10);
  });
  const [endDate, setEndDate] = useState(new Date().toISOString().slice(0, 10));
  const [dateField, setDateField] = useState("processed_at");
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (isSuperAdmin) {
      api.get("/admin/companies").then(r => {
        setCompanies(r.data);
        if (r.data.length > 0) setSelectedCompanyId(r.data[0].id);
      }).catch(() => {});
    }
  }, [isSuperAdmin]);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams({ date_field: dateField });
      if (startDate) params.set("start_date", startDate);
      if (endDate) params.set("end_date", endDate);

      let url;
      if (isSuperAdmin && selectedCompanyId) {
        url = `/reports/company/${selectedCompanyId}?${params}`;
      } else {
        url = `/reports/my-company?${params}`;
      }
      const r = await api.get(url);
      setReport(r.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to load report");
    } finally { setLoading(false); }
  }, [isSuperAdmin, selectedCompanyId, startDate, endDate, dateField]);

  const downloadCsv = () => {
    const params = new URLSearchParams({ date_field: dateField });
    if (startDate) params.set("start_date", startDate);
    if (endDate) params.set("end_date", endDate);
    const base = process.env.REACT_APP_BACKEND_URL + "/api";
    const url = isSuperAdmin && selectedCompanyId
      ? `${base}/reports/company/${selectedCompanyId}/csv?${params}`
      : `${base}/reports/my-company/csv?${params}`;
    const token = localStorage.getItem("token");
    // Fetch with auth header and trigger download
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `report_${selectedCompanyId || "company"}.csv`;
        a.click();
      });
  };

  return (
    <div className="space-y-6" data-testid="reports-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Company Reports</h2>
          <p className="text-sm text-slate-500 mt-0.5">Usage analytics, credit consumption, and overage tracking.</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={fetchReport}
            disabled={loading}
            data-testid="run-report-button"
            className="flex items-center gap-2 h-9 px-4 bg-[#006080] hover:bg-[#004B66] text-white text-sm font-medium rounded-md disabled:opacity-50 transition-colors"
          >
            <RefreshCw size={13} className={loading ? "animate-spin" : ""} />
            {loading ? "Loading..." : "Run Report"}
          </button>
          {report && (
            <button
              onClick={downloadCsv}
              data-testid="download-csv-button"
              className="flex items-center gap-2 h-9 px-4 border border-slate-200 text-sm font-medium rounded-md hover:bg-slate-50 transition-colors"
            >
              <Download size={13} /> CSV
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-lg p-4">
        <div className="flex flex-wrap gap-4 items-end">
          {isSuperAdmin && (
            <div>
              <label className="text-xs font-bold uppercase tracking-wide text-slate-500 block mb-1.5">Company</label>
              <select
                data-testid="report-company-select"
                value={selectedCompanyId}
                onChange={e => setSelectedCompanyId(e.target.value)}
                className="h-9 px-3 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#006080] bg-white min-w-48"
              >
                {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="text-xs font-bold uppercase tracking-wide text-slate-500 block mb-1.5">Start Date</label>
            <input
              type="date"
              data-testid="report-start-date"
              value={startDate}
              onChange={e => setStartDate(e.target.value)}
              className="h-9 px-3 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#006080]"
            />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wide text-slate-500 block mb-1.5">End Date</label>
            <input
              type="date"
              data-testid="report-end-date"
              value={endDate}
              onChange={e => setEndDate(e.target.value)}
              className="h-9 px-3 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#006080]"
            />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wide text-slate-500 block mb-1.5">Date Field</label>
            <div className="flex rounded-md border border-slate-200 overflow-hidden text-sm">
              {[{ val: "processed_at", label: "Processed" }, { val: "created_at", label: "Uploaded" }].map(opt => (
                <button
                  key={opt.val}
                  type="button"
                  data-testid={`date-field-${opt.val}`}
                  onClick={() => setDateField(opt.val)}
                  className={`h-9 px-3 font-medium transition-colors ${dateField === opt.val ? "bg-[#006080] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          <AlertCircle size={14} className="flex-shrink-0" /> {error}
        </div>
      )}

      {!report && !loading && (
        <div className="text-center py-20 bg-white border border-slate-200 rounded-lg">
          <BarChart2 size={32} className="text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-500 mb-4">Select filters above and click "Run Report" to generate a usage report.</p>
          <button onClick={fetchReport} className="h-9 px-4 bg-[#006080] text-white text-sm font-medium rounded-md hover:bg-[#004B66]">Run Report</button>
        </div>
      )}

      {report && (
        <div className="space-y-6" data-testid="report-content">
          {/* Company header */}
          <div className="flex items-center justify-between p-4 bg-white border border-slate-200 rounded-lg">
            <div>
              <p className="font-bold text-slate-900 text-lg">{report.company.name}</p>
              <p className="text-sm text-slate-500">{report.company.email}</p>
            </div>
            <div className="text-right text-xs text-slate-400">
              <p>Period: {report.date_range.start || "All time"} → {report.date_range.end || "Now"}</p>
              <p>By: {report.date_range.date_field === "processed_at" ? "Processing date" : "Upload date"}</p>
              <p>Generated: {new Date(report.generated_at).toLocaleString()}</p>
            </div>
          </div>

          {/* Summary stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard label="Total Documents" value={report.documents.total.toLocaleString()} Icon={FileText} />
            <StatCard label="Pages Processed" value={report.documents.pages_processed.toLocaleString()} Icon={BarChart2} accent />
            <StatCard label="Pipelines" value={report.pipelines.length} Icon={Zap} />
            <StatCard
              label="Overage Risk"
              value={
                report.credits.page_credits.overage > 0 || report.credits.ai_credits.overage > 0 ? "Yes" : "None"
              }
              sub={
                report.credits.page_credits.overage > 0
                  ? `+${report.credits.page_credits.overage} page credits`
                  : report.credits.ai_credits.overage > 0
                  ? `+${report.credits.ai_credits.overage} AI credits`
                  : "Within limits"
              }
              Icon={report.credits.page_credits.overage > 0 || report.credits.ai_credits.overage > 0 ? TrendingUp : TrendingDown}
            />
          </div>

          {/* Credits */}
          <div>
            <h3 className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-3">Credit Utilization</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <CreditBar
                label="Page Credits"
                used={report.credits.page_credits.used}
                total={report.credits.page_credits.total}
                overage={report.credits.page_credits.overage}
              />
              <CreditBar
                label="AI Credits"
                used={report.credits.ai_credits.used}
                total={report.credits.ai_credits.total}
                overage={report.credits.ai_credits.overage}
              />
            </div>
          </div>

          {/* Documents by status */}
          <div>
            <h3 className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-3">Documents by Status</h3>
            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
              {Object.keys(report.documents.by_status).length === 0 ? (
                <p className="text-sm text-slate-400 p-4">No documents in this period.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50">
                      <th className="text-left px-4 py-2.5 text-xs font-bold uppercase tracking-wide text-slate-500">Status</th>
                      <th className="text-right px-4 py-2.5 text-xs font-bold uppercase tracking-wide text-slate-500">Count</th>
                      <th className="text-right px-4 py-2.5 text-xs font-bold uppercase tracking-wide text-slate-500">Share</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(report.documents.by_status).map(([status, count]) => (
                      <tr key={status} className="border-b border-slate-100">
                        <td className="px-4 py-3">
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full capitalize ${STATUS_COLORS[status] || "bg-slate-100 text-slate-600"}`}>{status}</span>
                        </td>
                        <td className="px-4 py-3 text-right font-bold text-slate-900">{count.toLocaleString()}</td>
                        <td className="px-4 py-3 text-right text-slate-500">
                          {((count / Math.max(1, report.documents.total)) * 100).toFixed(1)}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* Pipeline breakdown */}
          {report.pipelines.length > 0 && (
            <div>
              <h3 className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-3">Pipeline Activity</h3>
              <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50">
                      <th className="text-left px-4 py-2.5 text-xs font-bold uppercase tracking-wide text-slate-500">Pipeline</th>
                      <th className="text-right px-4 py-2.5 text-xs font-bold uppercase tracking-wide text-slate-500">Calls</th>
                      <th className="text-right px-4 py-2.5 text-xs font-bold uppercase tracking-wide text-slate-500">Pages</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.pipelines.map(p => (
                      <tr key={p.pipeline_id} className="border-b border-slate-100">
                        <td className="px-4 py-3 font-medium text-slate-800">{p.pipeline_name}</td>
                        <td className="px-4 py-3 text-right text-slate-700">{p.calls.toLocaleString()}</td>
                        <td className="px-4 py-3 text-right text-slate-700">{p.pages.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
